'use client';
import { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import cn from 'classnames';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';

import './Link.style.scss';

type LinkVariant = 'link-l' | 'link-m' | 'link-m-wide' | 'link-s' | 'link-xs';

type LinkProps = {
  /** className to pass in for targeted styling */
  className?: string;

  /** URL to send the user to */
  href: string;

  /** Children to render. Can be a ReactNode. For plain text, use only a `string` */
  children: ReactNode;

  /** If true, opens the link in a new tab */
  newTab?: boolean;

  uppercase?: boolean;
  underline?: boolean;

  /** Normal text spacing */
  compact?: boolean;

  /** Event handler for when the user's mouse enters */
  onMouseEnter?: () => void;

  /** Event handler for when the user's mouse leaves */
  onMouseLeave?: () => void;

  /** Optional event handler to do something when the user clicks */
  onClick?: (e: MouseEventHandler<HTMLAnchorElement>) => void;

  /**
   *  Refer to the following table to determine the
   *  design system token to use
   *  | Name        | Design System Token                                    |
      |-------------|--------------------------------------------------------|
      | link-l      | $vds.font.rh.link.xs.500                               |
      | link-m      | $vds.font.rh.link.xxs.500                              |
      | link-m-wide | $vds.font.rh.link.xxs.500  - with wider letter-spacing |
      | link-s      | $vds.font.rh.link.xxs.700                              |
      | link-xs     | $vds.font.rh.link.xxs.600                              |
      @default 'link-m'
   */
  variant?: LinkVariant;

  target?: '_self' | '_blank' | '_parent' | '_top';

  style?: CSSProperties;

  /** If true, enables prefetch on hover. Defaults to true for better UX */
  prefetchOnHover?: boolean;

  'data-testid'?: string;
  'aria-current'?: boolean | 'page' | 'step' | 'location' | 'date' | 'time';
};

/**
 * Component used to render **text** links. Do not use for making
 * other components linkable
 */
function Link(props: LinkProps) {
  const {
    className = '',
    children,
    href,
    uppercase,
    underline,
    compact,
    newTab = false,
    variant = 'link-m',
    onClick,
    target,
    style,
    onMouseEnter,
    onMouseLeave,
    prefetchOnHover = true,
    'data-testid': dataTestId,
    'aria-current': ariaCurrent
  } = props;

  const router = useRouter();

  const newTabProps = newTab
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  // Next.js 13 double-encodes optional catch-all segments (e.g.
  // /apply/[[...slug]]) in the client chunk manifest, so SPA navigation or
  // prefetch into them 404s the chunk (ChunkLoadError) and dumps the user on a
  // broken page. Force a hard navigation for those routes so the
  // server-rendered page loads cleanly. Remove when on Next 14+ (also see
  // ChunkErrorReloader). The apply route is the only such catch-all today.
  const targetsApplyRoute = /(^|\/)apply(\/|$|\?)/.test(href.split('#')[0]);

  const handleMouseEnter = () => {
    if (prefetchOnHover && href && !targetsApplyRoute) {
      router.prefetch(href);
    }
    onMouseEnter?.();
  };

  if (targetsApplyRoute) {
    return (
      <a
        className={cn('Link', className, variant, {
          uppercase,
          underline,
          compact
        })}
        href={href}
        target={target}
        style={style}
        data-testid={dataTestId}
        aria-current={ariaCurrent}
        onClick={(event) => {
          onClick?.(event as unknown as MouseEventHandler<HTMLAnchorElement>);
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={onMouseLeave}
        {...newTabProps}
      >
        {children}
      </a>
    );
  }

  return (
    <NextLink
      className={cn('Link', className, variant, {
        uppercase,
        underline,
        compact
      })}
      href={href}
      target={target}
      style={style}
      data-testid={dataTestId}
      aria-current={ariaCurrent}
      onClick={(event) => {
        // I can't find the type to cast to. Just doing an unknown cast to force it here
        onClick?.(event as unknown as MouseEventHandler<HTMLAnchorElement>);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onMouseLeave}
      prefetch={false}
      {...newTabProps}
    >
      {children}
    </NextLink>
  );
}

export default Link;
